// src/screens/s3/StressTest.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Session 3 — Stress Test Scenario
// What if investment returns are lower than expected?
// Always shown. No skip. No commit. hideCommitButtons=true
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import { ClientData } from '../../types/ClientData';
import ScenarioScreenWrapper from './ScenarioScreenWrapper';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

export default function StressTest({ data, onUpdate }: Props) {
  const voyantNote = `Run the plan at 4% nominal growth instead of 8%. Is the plan still funded to 82? Note the result.`;

  const resultCard = data.s3StressTestResult === 'funded'
    ? <div className="bg-green-50 border border-green-200 rounded-lg p-6">
        <p className="text-sm font-medium text-green-800 mb-1">✓ Plan is funded</p>
        <p className="text-xs text-green-700">Even at lower growth, your plan reaches age 82</p>
      </div>
    : data.s3StressTestResult === 'shortfall'
      ? <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
          <p className="text-sm font-medium text-amber-800 mb-1">⚠ Shortfall identified</p>
          <p className="text-xs text-amber-700">{data.s3StressTestNote}</p>
        </div>
      : null;

  return (
    <ScenarioScreenWrapper
      title="Stress Test"
      question="Let's make sure this plan is robust. Investment returns are never guaranteed — what happens if they're lower than expected?"
      clientName={data.clientName}
      voyantNote={voyantNote}
      isCommitted={false}
      hideCommitButtons={true}
      voyantReadbacks={
        <div className="space-y-4">
          <div>
            <label className="text-xs text-gray-400 uppercase tracking-wide font-medium block mb-2">
              Test Result
            </label>
            <select
              value={data.s3StressTestResult}
              onChange={(e) => onUpdate({ s3StressTestResult: e.target.value as 'funded' | 'shortfall' | '' })}
              className="w-full border border-gray-200 rounded-lg px-4 py-2.5 bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-ma-navy/20"
            >
              <option value="">Select test outcome...</option>
              <option value="funded">Funded at 4% growth</option>
              <option value="shortfall">Shortfall identified</option>
            </select>
          </div>

          {data.s3StressTestResult && (
            <>
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wide font-medium block mb-2">
                  Summary Note
                </label>
                <textarea
                  value={data.s3StressTestNote}
                  onChange={(e) => onUpdate({ s3StressTestNote: e.target.value })}
                  placeholder="Summarize the Voyant findings..."
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-gray-800 focus:outline-none focus:ring-2 focus:ring-ma-navy/20 resize-none"
                  rows={3}
                />
              </div>

              {resultCard}
            </>
          )}
        </div>
      }
    >
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>Adviser Instruction:</strong> Run a projection at 4% nominal growth (instead of the base case 8%). Check whether the plan is still funded to age 82.
        </p>
      </div>
    </ScenarioScreenWrapper>
  );
}
